import { useState, useMemo, useEffect } from 'react';
import { ENERGY_ICONS, ENERGY_QUESTIONS } from '../config/energy.config';
import { EnergyCheckState } from '../types/energy.types';
import { evaluateEnergyCheck } from '../utils/energyEvaluation';
import { EnergyIconSelector } from '../components/EnergyIconSelector';
import { EnergyQuestions } from '../components/EnergyQuestions';
import { EnergyResult } from '../components/EnergyResult';
import { EnergyWheel } from '../components/EnergyWheel';

const EnergyCheck = () => {
  const [state, setState] = useState<EnergyCheckState>({
    selectedIcons: new Set(),
    answers: {},
    site: '',
    task: '',
    peopleAtRisk: 'unknown',
    notes: '',
  });

  const [saveMessage, setSaveMessage] = useState('');
  const [showWheel, setShowWheel] = useState(false);

  const activeQuestions = useMemo(() => {
    return ENERGY_QUESTIONS.filter((q) => state.selectedIcons.has(q.icon));
  }, [state.selectedIcons]);

  const result = useMemo(() => {
    return evaluateEnergyCheck(state);
  }, [state]);

  // Initialize answers when questions become active
  useEffect(() => {
    const newAnswers = { ...state.answers };
    let changed = false;

    activeQuestions.forEach((q) => {
      if (!(q.id in newAnswers)) {
        newAnswers[q.id] = q.default || '';
        changed = true;
      }
    });

    if (changed) {
      setState((prev) => ({ ...prev, answers: newAnswers }));
    }
  }, [activeQuestions, state.answers]);

  const handleIconToggle = (iconId: string) => {
    setState((prev) => {
      const newSelected = new Set(prev.selectedIcons);
      if (newSelected.has(iconId)) {
        newSelected.delete(iconId);
      } else {
        newSelected.add(iconId);
      }
      return { ...prev, selectedIcons: newSelected };
    });
  };

  const handleAnswerChange = (questionId: string, value: string) => {
    setState((prev) => ({
      ...prev,
      answers: { ...prev.answers, [questionId]: value },
    }));
  };

  const handleSubmit = async () => {
    // TODO: Integrate with myosh API
    const submission = {
      site_area: state.site,
      task: state.task,
      people_at_risk: state.peopleAtRisk,
      notes: state.notes,
      energy_icons: Array.from(state.selectedIcons),
      energy_rating: result.level,
      trigger_codes: result.triggers.map((t) => t.code),
      trigger_text: result.triggers.map((t) => t.text),
      controls_required: result.controls,
    };

    console.log('Energy Check Submission:', submission);

    setSaveMessage('Saved successfully!');
    setTimeout(() => setSaveMessage(''), 3000);
  };

  const handleReset = () => {
    setState({
      selectedIcons: new Set(),
      answers: {},
      site: '',
      task: '',
      peopleAtRisk: 'unknown',
      notes: '',
    });
    setSaveMessage('');
  };

  return (
    <div className="bg-gray-5 min-h-screen">
      <header className="shadow-md" style={{ backgroundColor: '#1e3a8a' }}>
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold" style={{ color: '#ffffff' }}>
            Energy Check
          </h1>
          <p className="mt-3 text-base" style={{ color: '#f0f0f0' }}>
            1. Describe your task → 2. Select energy hazards → 3. Answer safety
            questions → 4. Get instant rating
          </p>
        </div>
      </header>

      <main className="mx-auto grid max-w-7xl grid-cols-1 gap-4 px-4 py-6 lg:grid-cols-3 lg:px-6">
        <section className="lg:col-span-2">
          <div className="border-gray-4 bg-mono-1 rounded-2xl border p-6 shadow-lg">
            <h2 className="text-primary-1 mb-4 text-lg font-semibold">
              1) Task Context
            </h2>
            <p className="text-gray-2 mb-4 text-xs">
              Start by describing what you&apos;re about to do and where. This
              helps identify relevant hazards.
            </p>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="site"
                  className="text-gray-2 text-xs font-medium"
                >
                  Site / Area
                </label>
                <input
                  id="site"
                  type="text"
                  value={state.site}
                  onChange={(e) =>
                    setState((prev) => ({ ...prev, site: e.target.value }))
                  }
                  placeholder="e.g., Workshop Bay 3"
                  className="border-gray-4 bg-mono-1 text-gray-1 focus:border-primary-3 rounded-lg border px-3 py-2 text-sm outline-none"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label
                  htmlFor="task"
                  className="text-gray-2 text-xs font-medium"
                >
                  Task
                </label>
                <input
                  id="task"
                  type="text"
                  value={state.task}
                  onChange={(e) =>
                    setState((prev) => ({ ...prev, task: e.target.value }))
                  }
                  placeholder="e.g., Replace hydraulic hose"
                  className="border-gray-4 bg-mono-1 text-gray-1 focus:border-primary-3 rounded-lg border px-3 py-2 text-sm outline-none"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label
                  htmlFor="people"
                  className="text-gray-2 text-xs font-medium"
                >
                  People at risk
                </label>
                <select
                  id="people"
                  value={state.peopleAtRisk}
                  onChange={(e) =>
                    setState((prev) => ({
                      ...prev,
                      peopleAtRisk: e.target.value,
                    }))
                  }
                  className="border-gray-4 bg-mono-1 text-gray-1 focus:border-primary-3 rounded-lg border px-3 py-2 text-sm outline-none"
                >
                  <option value="unknown">Unknown</option>
                  <option value="yes">Yes (people could be exposed)</option>
                  <option value="no">No (isolated/no exposure)</option>
                </select>
              </div>

              <div className="flex flex-col gap-2">
                <label
                  htmlFor="notes"
                  className="text-gray-2 text-xs font-medium"
                >
                  Notes
                </label>
                <textarea
                  id="notes"
                  value={state.notes}
                  onChange={(e) =>
                    setState((prev) => ({ ...prev, notes: e.target.value }))
                  }
                  placeholder="Short description, controls already in place, etc."
                  className="border-gray-4 bg-mono-1 text-gray-1 focus:border-primary-3 min-h-[70px] resize-y rounded-lg border px-3 py-2 text-sm outline-none"
                />
              </div>
            </div>

            <div className="bg-gray-4 my-6 h-px" />

            <h2 className="text-primary-1 mb-4 text-lg font-semibold">
              2) Energy Hazards
            </h2>
            <p className="text-gray-2 mb-4 text-xs">
              Select all energy types present in this task. Click each icon to
              select/deselect.
            </p>

            {/* Energy Wheel - Collapsible */}
            <div className="mb-4">
              <button
                onClick={() => setShowWheel(!showWheel)}
                className="text-primary-2 hover:text-primary-1 flex items-center gap-2 text-sm font-medium transition"
              >
                <span
                  className={`transform transition-transform ${showWheel ? 'rotate-90' : ''}`}
                >
                  ▶
                </span>
                {showWheel ? 'Hide' : 'Show'} Energy Wheel
                <span className="bg-primary-4 text-primary-1 ml-1 rounded-full px-2 py-0.5 text-xs font-semibold">
                  Reference
                </span>
              </button>

              {showWheel && (
                <div className="bg-gray-5 border-gray-4 mt-4 rounded-xl border p-6">
                  <EnergyWheel
                    icons={ENERGY_ICONS}
                    selectedIcons={state.selectedIcons}
                  />
                </div>
              )}
            </div>

            <EnergyIconSelector
              icons={ENERGY_ICONS}
              selectedIcons={state.selectedIcons}
              onToggle={handleIconToggle}
            />

            <div className="bg-gray-4 my-6 h-px" />

            <h2 className="text-primary-1 mb-4 text-lg font-semibold">
              3) Safety Assessment
            </h2>
            <p className="text-gray-2 mb-3 text-xs">
              For each hazard selected, answer questions about magnitude,
              exposure, and controls. If unsure, select &quot;Not sure&quot; and
              escalate to supervisor.
            </p>
            <EnergyQuestions
              questions={activeQuestions}
              answers={state.answers}
              onChange={handleAnswerChange}
              icons={ENERGY_ICONS}
            />

            <div className="bg-gray-4 my-6 h-px" />

            <div className="flex flex-col gap-2">
              <label
                htmlFor="notes"
                className="text-gray-2 text-xs font-medium"
              >
                Additional Notes (optional)
              </label>
              <textarea
                id="notes"
                value={state.notes}
                onChange={(e) =>
                  setState((prev) => ({ ...prev, notes: e.target.value }))
                }
                placeholder="Short description, controls already in place, specific concerns, etc."
                className="border-gray-4 bg-mono-1 text-gray-1 focus:border-primary-3 min-h-[70px] resize-y rounded-lg border px-3 py-2 text-sm outline-none"
              />
            </div>

            <div className="bg-gray-4 my-6 h-px" />

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={handleSubmit}
                disabled={state.selectedIcons.size === 0}
                className="rounded-xl border-2 border-blue-700 bg-blue-700 px-8 py-4 text-base font-bold text-white shadow-lg transition hover:border-blue-800 hover:bg-blue-800 hover:shadow-xl active:bg-blue-900 disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-300 disabled:text-gray-500 disabled:shadow-none"
              >
                {result.level === 'RED'
                  ? '🔴 Submit (Supervisor Approval Required)'
                  : 'Submit'}
              </button>
              <button
                onClick={handleReset}
                className="rounded-xl bg-white px-6 py-4 text-base font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
              >
                Reset Form
              </button>
              {saveMessage && (
                <div className="text-success text-sm font-medium">
                  {saveMessage}
                </div>
              )}
            </div>
            {state.selectedIcons.size === 0 && (
              <p className="text-gray-3 mt-2 text-xs italic">
                Select at least one energy hazard to enable submission
              </p>
            )}
          </div>
        </section>

        <aside className="lg:col-span-1">
          <div className="border-gray-4 bg-mono-1 sticky top-6 rounded-2xl border p-6 shadow-lg">
            <h2 className="text-primary-1 mb-4 text-lg font-semibold">
              Result
            </h2>
            <EnergyResult
              result={result}
              iconCount={state.selectedIcons.size}
            />
          </div>
        </aside>
      </main>
    </div>
  );
};

export default EnergyCheck;
